/**
 * 画面遷移の状態機械 — 純粋JS（DOM / three.js 非依存、node でテスト可能）。
 *
 * 画面: 'title' → 'setup' → 'game' →（勝利バナー / ☰メニュー）→
 *       再戦(game) or セットアップ or タイトル
 *       'title' → 'tutorial'（チュートリアルモード。☰メニューから離脱、
 *       全レッスン完了後は CPU 対局(game) or タイトルへ）
 *
 * game / tutorial 画面内のオーバーレイ状態もここで一元管理する:
 *   - menuOpen        … ☰メニュー表示中（盤入力を止め、CPUタイマーを保留）
 *   - resignedWinner  … 投了による勝者（core の state.winner は触らない。
 *                        UI 層だけで「決着済み」として扱う）
 *
 * 実際の DOM 表示・タイマー操作は main.js が derive() の結果を反映する。
 */

/** 有効な画面 id 一覧。 */
export const SCREENS = ['title', 'setup', 'stageSelect', 'game', 'tutorial'];

/**
 * 状態機械を生成する。初期画面はタイトル。
 *
 * 遷移メソッドはどの画面から呼ばれても安全（不正な遷移は無視 or 状態を
 * 正規化する）。toTitle / toSetup / startGame はオーバーレイ状態
 * （menuOpen / resignedWinner）を必ずクリアする。
 */
export function createFlow() {
  let screen = 'title';
  let menuOpen = false;
  let resignedWinner = null;

  function clearOverlays() {
    menuOpen = false;
    resignedWinner = null;
  }

  return {
    get screen() {
      return screen;
    },
    get menuOpen() {
      return menuOpen;
    },
    get resignedWinner() {
      return resignedWinner;
    },

    /** タイトル画面へ（メニュー・投了状態はクリア）。 */
    toTitle() {
      screen = 'title';
      clearOverlays();
    },

    /** セットアップ画面へ（メニュー・投了状態はクリア）。 */
    toSetup() {
      screen = 'setup';
      clearOverlays();
    },

    /** ステージ選択画面へ（シングルモード。メニュー・投了状態はクリア）。 */
    toStageSelect() {
      screen = 'stageSelect';
      clearOverlays();
    },

    /** 対局開始 / 再戦。resignedWinner を含む前局の状態をクリアする。 */
    startGame() {
      screen = 'game';
      clearOverlays();
    },

    /** チュートリアル画面へ（メニュー・投了状態はクリア）。 */
    toTutorial() {
      screen = 'tutorial';
      clearOverlays();
    },

    /** ☰メニューを開く。game / tutorial 画面でのみ有効。開けたら true。 */
    openMenu() {
      if (screen !== 'game' && screen !== 'tutorial') return false;
      menuOpen = true;
      return true;
    },

    /** ☰メニューを閉じる（つづける / Esc / 背景クリック）。 */
    closeMenu() {
      menuOpen = false;
    },

    /**
     * 投了を確定する。winner は勝者インデックス（0/1、resignWinner() で決定）。
     * game 画面でのみ有効。確定したらメニューを閉じて true を返す。
     */
    resign(winner) {
      if (screen !== 'game' || winner !== 0 && winner !== 1) return false;
      resignedWinner = winner;
      menuOpen = false;
      return true;
    },

    /** 現在状態のプレーンなスナップショット（derive() に渡す）。 */
    snapshot() {
      return { screen, menuOpen, resignedWinner };
    },
  };
}

/**
 * スナップショット → 各 UI 要素の表示可否・入力可否（純粋関数）。
 *
 *   titleVisible      … タイトル画面
 *   setupVisible      … セットアップ画面（カスタムモード）
 *   stageSelectVisible… ステージ選択画面（シングルモード）
 *   tutorialVisible   … チュートリアル画面（指示パネル・進捗の表示）
 *   menuButtonVisible … 右上の☰ボタン（game / tutorial 画面）
 *   menuVisible       … ☰メニューのオーバーレイ
 *   guideAllowed      … 画面下ガイドバー（game / tutorial かつメニュー非表示。
 *                        勝敗確定時の非表示は guideMessage 側の責務）
 *   boardInputAllowed … 盤クリック・能力ボタン等の人間入力
 *                       （game / tutorial・メニュー閉・投了未確定のとき）
 *   cpuHold           … CPU の思考タイマー / 着手コミットを保留すべきか
 *                       （game 以外・メニュー中・投了後は真。tutorial に
 *                        CPU 駆動はない）
 *   meterAllowed      … 形勢メーター（game 画面のみ。tutorial では非表示。
 *                        オン/オフ設定との AND は呼び出し側）
 */
export function derive({ screen, menuOpen, resignedWinner }) {
  const inGame = screen === 'game';
  const inTutorial = screen === 'tutorial';
  const onBoard = inGame || inTutorial;
  return {
    titleVisible: screen === 'title',
    setupVisible: screen === 'setup',
    stageSelectVisible: screen === 'stageSelect',
    tutorialVisible: inTutorial,
    menuButtonVisible: onBoard,
    menuVisible: onBoard && menuOpen,
    guideAllowed: onBoard && !menuOpen,
    boardInputAllowed: onBoard && !menuOpen && resignedWinner === null,
    cpuHold: !inGame || menuOpen || resignedWinner !== null,
    meterAllowed: inGame,
  };
}

/**
 * 投了時の勝者を決める（純粋関数）。
 *
 *   isCpu   … [先手がCPUか, 後手がCPUか]（真偽値の配列）
 *   current … 現在の手番（0/1）
 *
 * - 人間 vs CPU  → CPU 側の勝ち（投了できるのは人間だけ）
 * - 人間 vs 人間 → 手番側が投了 → 相手の勝ち
 * - CPU  vs CPU  → null（投了は不可。観戦はメニューの「観戦をやめる」で離脱）
 */
export function resignWinner(isCpu, current) {
  const humanCount = isCpu.filter((c) => !c).length;
  if (humanCount === 0) return null;
  if (humanCount === 2) return 1 - current;
  return isCpu[0] ? 0 : 1;
}
